import { useState } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "../data/company";

export default function ContactForm({ defaultService = "", backgroundClass = "bg-white" }) {
  const { pathname } = useLocation();
  const formBackgroundClass = pathname.startsWith("/ydelser/")
    ? "bg-[#FAFAF8] [&_input]:bg-white [&_select]:bg-white [&_textarea]:bg-white"
    : backgroundClass;
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService,
    message: "",
    website_url: "", // Honeypot
  });

  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus("error");
      setErrorMessage("Udfyld venligst alle påkrævede felter.");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          service: defaultService,
          message: "",
          website_url: "",
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus("error");
        setErrorMessage(data.message || "Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring direkte.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Kunne ikke oprette forbindelse. Kontakt venligst Andreas direkte på telefon " + COMPANY.phone);
    }
  };

  if (status === "success") {
    return (
      <div className="bg-white rounded-lg border border-emerald-200 p-8 sm:p-10 shadow-sm text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4 text-2xl">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-[#111111]">Mange tak for din henvendelse!</h3>
        <p className="text-neutral-600 mt-2 max-w-md mx-auto text-sm sm:text-base">
          Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
        </p>
        <div className="mt-6 pt-6 border-t border-neutral-100 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
          <span className="text-neutral-500">Haster det? Ring direkte:</span>
          <a
            href={COMPANY.phoneLink}
            className="font-bold text-[#111111] hover:text-[#A78E51] flex items-center gap-1.5"
          >
            <i className="fa-solid fa-phone text-[#A78E51]" aria-hidden="true" />
            <span>{COMPANY.phone}</span>
          </a>
        </div>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center text-xs font-semibold text-neutral-500 hover:text-[#111111] underline"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`${formBackgroundClass} rounded-lg border border-neutral-200 p-6 sm:p-8 shadow-sm space-y-5`}
      noValidate
    >
      {/* Honeypot field (hidden from humans via sr-only + absolute) */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Website URL (leave empty)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {status === "error" && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
          <i className="fa-solid fa-triangle-exclamation text-red-600 mt-0.5" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Navn */}
      <div>
        <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
          Navn <span className="text-[#A78E51]">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          placeholder="Dit fulde navn"
          value={formData.name}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-md border border-neutral-300 bg-[#FAFAF8] text-[#111111] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:border-transparent text-sm transition-all"
        />
      </div>

      {/* Telefon og Email i grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
            Telefonnummer <span className="text-[#A78E51]">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            placeholder="f.eks. 61 28 71 53"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-md border border-neutral-300 bg-[#FAFAF8] text-[#111111] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:border-transparent text-sm transition-all"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
            Email <span className="text-[#A78E51]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            placeholder="din@email.dk"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-md border border-neutral-300 bg-[#FAFAF8] text-[#111111] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:border-transparent text-sm transition-all"
          />
        </div>
      </div>

      {/* Valg af ydelse */}
      <div>
        <label htmlFor="service" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
          Hvad drejer opgaven sig om?
        </label>
        <select
          id="service"
          name="service"
          value={formData.service}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-md border border-neutral-300 bg-[#FAFAF8] text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:border-transparent text-sm transition-all"
        >
          <option value="">Vælg en ydelse (valgfrit)</option>
          <option value="Døre og vinduer">Døre og vinduer (vores store passion)</option>
          <option value="Træterrasser og udendørsarealer">Træterrasser & udendørsarealer</option>
          <option value="Indendørs renovering">Indendørs renovering (lofter & vægge)</option>
          <option value="Andet tømrerarbejde">Andet tømrerarbejde</option>
        </select>
      </div>

      {/* Beskrivelse */}
      <div>
        <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
          Beskrivelse af opgaven <span className="text-[#A78E51]">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          required
          placeholder="Beskriv kort din opgave, ønsker til materialer eller adresse..."
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-md border border-neutral-300 bg-[#FAFAF8] text-[#111111] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#A78E51] focus:border-transparent text-sm transition-all resize-y"
        />
      </div>

      {/* Submit button */}
      <div>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-md bg-[#A78E51] text-white font-extrabold text-sm hover:bg-[#937B43] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A78E51] transition-colors disabled:opacity-50 shadow-sm"
        >
          {status === "submitting" ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>Sender forespørgsel...</span>
            </>
          ) : (
            <>
              <span>Send forespørgsel</span>
              <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
            </>
          )}
        </button>
      </div>

      <div className="flex items-center justify-between text-xs text-neutral-500 pt-1">
        <span>* Påkrævede felter</span>
        <span className="flex items-center gap-1 text-neutral-400">
          <i className="fa-solid fa-lock text-[10px]" aria-hidden="true" />
          <span>Direkte henvendelse til Andreas</span>
        </span>
      </div>
    </form>
  );
}
